/**
 * Public portal API service.
 * Uses a plain axios instance WITHOUT the authenticated interceptors
 * (no x-cerberus-token, no x-organization-id headers).
 */
import axios from 'axios';
import { PublicSignInfo, OtpVerifyResponse } from './types';
import { PORTAL_ENDPOINTS, PORTAL_REQUEST_TIMEOUT_MS, PORTAL_TIMEOUT_MESSAGE } from './constants';

const publicAxios = axios.create({
    baseURL: process.env.REACT_APP_AICA_URL,
    timeout: PORTAL_REQUEST_TIMEOUT_MS,
});

// Normalise a client-side timeout into a friendly, recognisable message — the gateway timeout is
// a ceiling, not a target. Every other error passes through unchanged.
publicAxios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
            return Promise.reject(new Error(PORTAL_TIMEOUT_MESSAGE));
        }
        return Promise.reject(error);
    },
);

/** True when `error` is the normalised timeout error raised by the interceptor above. */
export function isPortalTimeoutError(error: unknown): boolean {
    return error instanceof Error && error.message === PORTAL_TIMEOUT_MESSAGE;
}

/** Raw shape serialized by MagicSignPublicInfoResponse (Java backend). */
interface BackendPublicSignInfo {
    state: 'ACTIVE' | 'EXPIRED' | 'CANCELLED' | 'ALREADY_SIGNED';
    pnoBranding?: { name?: string; logoUrl?: string; accent?: string; supportContact?: string };
    documentType?: string;
    companyName?: string;
    signatoryName?: string;
    signatoryEmail?: string;
    actionLabel?: string;
    signType?: string;
    otpChannel?: string;
}

/**
 * Maps the raw backend shape (MagicSignPublicInfoResponse) to the FE domain type (PublicSignInfo).
 * All field renames happen here — the rest of the FE reads the stable domain type only.
 *   backend `state`                   → FE `status`
 *   backend `pnoBranding.name`        → FE `pnoName`
 *   backend `pnoBranding.logoUrl`     → FE `pnoLogo`
 *   backend `documentType`            → FE `documentName`
 *   backend `companyName`             → FE `companyName` (same)
 *   backend `signatoryName`           → FE `signatoryName` (same)
 *   backend `signatoryEmail`          → FE `signatoryEmail` (same)
 *   backend `actionLabel`             → FE `actionLabel` (same)
 * Fields absent from the backend response (`consentTemplate`, `token`) remain undefined/passed-in.
 */
function mapPublicSignInfo(raw: BackendPublicSignInfo, token: string): PublicSignInfo {
    return {
        token,
        status: raw.state,
        pnoName: raw.pnoBranding?.name,
        pnoLogo: raw.pnoBranding?.logoUrl,
        documentName: raw.documentType,
        companyName: raw.companyName,
        signatoryName: raw.signatoryName,
        signatoryEmail: raw.signatoryEmail,
        actionLabel: raw.actionLabel,
        // consentTemplate is not returned by the backend; the hook falls back to a default string.
    };
}

export async function fetchPublicSignInfo(token: string): Promise<PublicSignInfo> {
    const res = await publicAxios.get(`${PORTAL_ENDPOINTS.INFO}/${token}`);
    const raw: BackendPublicSignInfo = res.data?.data ?? res.data;
    return mapPublicSignInfo(raw, token);
}

export async function requestOtp(token: string): Promise<void> {
    await publicAxios.post(PORTAL_ENDPOINTS.OTP_REQUEST, { token });
}

export async function verifyOtp(token: string, otp: string): Promise<OtpVerifyResponse> {
    const res = await publicAxios.post(PORTAL_ENDPOINTS.OTP_VERIFY, { token, otp });
    // Design A (RECUR-26495): backend now returns the cerberus JWT as the `data` string.
    // The FE sends it as x-cerberus-token on the /public/sign call — no bespoke sessionToken.
    const authToken: string = typeof res.data?.data === 'string' ? res.data.data : res.data?.data?.authToken ?? res.data?.authToken ?? '';
    return { authToken };
}

export async function fetchPublicDocument(token: string, _unused = '', signed = false): Promise<string> {
    // Document is viewable for review by the opaque token alone (OTP is enforced only at sign).
    // signed=true returns the signed copy (stamped signatures) for the post-sign view/download.
    // Design A: no sessionToken is passed here — document access is token-gated only.
    const res = await publicAxios.get(`${PORTAL_ENDPOINTS.DOCUMENT}/${token}`, {
        params: { ...(signed ? { signed: 'true' } : {}) },
        responseType: 'blob',
    });
    const blob: Blob = res.data;
    // The backend may return a 200 with a JSON error envelope (AicaResponse) instead of the PDF.
    // Detect that and surface the real message rather than handing the viewer a broken "PDF".
    if (blob && blob.type && blob.type.indexOf('application/pdf') === -1) {
        let message = 'Failed to load document.';
        try {
            const parsed = JSON.parse(await blob.text());
            message = parsed?.responseData?.responseMessage || parsed?.message || parsed?.error || message;
        } catch {
            /* non-JSON body — keep the default message */
        }
        throw new Error(message);
    }
    // Use the blob as-is so its real content type is preserved.
    return URL.createObjectURL(blob);
}

export interface SignGeo {
    latitude?: number;
    longitude?: number;
}

/**
 * Design A (RECUR-26495): authToken is the cerberus JWT returned by verifyOtp().
 * It is sent as x-cerberus-token so AuthInterceptor can validate it and populate MDC
 * with the caller's identity. sessionToken is no longer included in the request body.
 */
export async function submitSign(token: string, authToken: string, consent: boolean, geo: SignGeo = {}): Promise<void> {
    await publicAxios.post(
        PORTAL_ENDPOINTS.SIGN,
        { token, consent, latitude: geo.latitude, longitude: geo.longitude },
        { headers: { 'x-cerberus-token': authToken } },
    );
}

export async function downloadSignedDocument(token: string): Promise<void> {
    // After signing the session token is cleared server-side; the document remains downloadable
    // by the opaque token alone. signed=true returns the signed copy.
    const res = await publicAxios.get(`${PORTAL_ENDPOINTS.DOCUMENT}/${token}`, {
        responseType: 'blob',
        params: { signed: 'true' },
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'signed-document.pdf';
    a.click();
    URL.revokeObjectURL(url);
}
